import { z } from "zod";
import { moneyMinor, optionalText, uuid } from "./common";

/** A reference to a secret, never the secret: env://NAME (single server) or a secret-manager URI. */
export const secretRef = z
  .string()
  .trim()
  .regex(/^((vault|awssm|gcpsm|azkv):\/\/[A-Za-z0-9/_.:-]+|env:\/\/[A-Z][A-Z0-9_]{2,63})$/, "Use env://NAME or a secret-manager reference, never the key itself");

export const gatewayAccountInput = z.object({
  provider: z.literal("razorpay"),
  displayName: z.string().trim().min(1).max(80).default("Razorpay"),
  keyId: z.string().trim().regex(/^rzp_(test|live)_[A-Za-z0-9]{6,40}$/, "Razorpay key ids look like rzp_test_... or rzp_live_..."),
  credentialsSecretRef: secretRef,
  webhookSecretRef: secretRef.nullish().transform((v) => v ?? null),
  isActive: z.boolean().default(true),
  /** Required when an account exists (optimistic locking). */
  version: z.number().int().positive().optional(),
});
export type GatewayAccountInput = z.input<typeof gatewayAccountInput>;

export interface GatewayStatus {
  /** True when an active account exists and its key secret resolves. */
  configured: boolean;
  account: {
    id: string;
    provider: "razorpay";
    mode: "test" | "live";
    displayName: string;
    keyId: string;
    credentialsSecretRef: string;
    webhookSecretRef: string | null;
    isActive: boolean;
    lastCheckedAt: string | null;
    lastCheckError: string | null;
    version: number;
  } | null;
  /** Path to register in the Razorpay dashboard (prefix with the API's public address). */
  webhookPath: string | null;
  problems: string[];
}

export const PAYMENT_LINK_STATUSES = ["created", "paid", "cancelled", "expired"] as const;
export type PaymentLinkStatus = (typeof PAYMENT_LINK_STATUSES)[number];

export const createPaymentLinkInput = z.object({
  patientId: uuid,
  /** Pay this invoice's balance (or part of it); without it, the money is an advance. */
  invoiceId: uuid.nullish().transform((v) => v ?? null),
  /** Defaults to the invoice balance. Required for an advance. */
  amountMinor: moneyMinor.positive().nullish().transform((v) => v ?? null),
  branchId: uuid.nullish().transform((v) => v ?? null),
  description: optionalText(200),
  /** Days the link stays valid. */
  expiresInDays: z.number().int().min(1).max(30).default(7),
  /** Also message the link to the patient on WhatsApp (or put it in "To send" when no number is connected). */
  sendWhatsApp: z.boolean().default(true),
});
export type CreatePaymentLinkInput = z.input<typeof createPaymentLinkInput>;

export const paymentLinkListQuery = z.object({
  patientId: uuid.optional(),
  invoiceId: uuid.optional(),
  status: z.enum([...PAYMENT_LINK_STATUSES, "all"]).default("all"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface PaymentLink {
  id: string;
  patient: { id: string; fullName: string; patientCode: string };
  invoice: { id: string; number: string | null } | null;
  amountMinor: number;
  currency: string;
  description: string;
  status: PaymentLinkStatus;
  url: string | null;
  providerLinkId: string | null;
  expiresAt: string;
  paidAt: string | null;
  /** Receipt created from the payment. */
  payment: { id: string; number: string } | null;
  messageStatus: string | null;
  createdBy: string | null;
  createdAt: string;
}
